import { ConflictResponse } from './types';

/**
 * Log entry for a conflict resolution attempt
 */
export interface ConflictLogEntry {
    timestamp: string;
    entityType: string;
    entityId: string;
    conflictType: string;
    attemptNumber: number;
    expectedVersion: number;
    actualVersion?: number;
    resolved: boolean;
    error?: string;
}

/**
 * Statistics about conflict resolution
 */
export interface ConflictStats {
    total: number;
    resolved: number;
    failed: number;
    byEntityType: Record<string, number>;
    byConflictType: Record<string, number>;
    averageRetries: number;
}

/**
 * Logger for tracking conflict resolution attempts and outcomes.
 * Provides detailed logging and statistics for debugging and monitoring.
 */
export class ConflictLogger {
    private logs: ConflictLogEntry[] = [];
    private maxLogSize = 1000; // Keep last 1000 entries

    /**
     * Log a conflict resolution attempt.
     * 
     * @param conflict - The conflict response
     * @param attemptNumber - Which retry attempt this is (0 = first attempt)
     * @param resolved - Whether the conflict was successfully resolved
     * @param error - Optional error message if resolution failed
     */
    logConflict(
        conflict: ConflictResponse,
        attemptNumber: number,
        resolved: boolean,
        error?: string
    ): void {
        const entry: ConflictLogEntry = {
            timestamp: new Date().toISOString(),
            entityType: conflict.entityType,
            entityId: conflict.entityId,
            conflictType: conflict.conflictType,
            attemptNumber,
            expectedVersion: conflict.expectedVersion,
            actualVersion: conflict.actualVersion,
            resolved,
            error
        };

        this.logs.push(entry);

        // Trim logs if exceeding max size
        if (this.logs.length > this.maxLogSize) {
            this.logs = this.logs.slice(-this.maxLogSize);
        }

        // Console logging
        const level = resolved ? 'info' : 'error';
        const prefix = resolved ? '✓' : '✗';
        console[level](
            `[Conflict Resolution] ${prefix} ${conflict.entityType}#${conflict.entityId}`,
            {
                conflictType: conflict.conflictType,
                attemptNumber,
                expectedVersion: conflict.expectedVersion,
                actualVersion: conflict.actualVersion,
                resolved,
                error
            }
        );
    }

    /**
     * Get statistics about conflict resolution.
     * 
     * @returns Aggregated statistics
     */
    getStats(): ConflictStats {
        const byEntityType: Record<string, number> = {};
        const byConflictType: Record<string, number> = {};
        let totalRetries = 0;

        for (const log of this.logs) {
            // Count by entity type
            byEntityType[log.entityType] = (byEntityType[log.entityType] || 0) + 1;
            
            // Count by conflict type
            byConflictType[log.conflictType] = (byConflictType[log.conflictType] || 0) + 1;
            
            // Sum retries
            totalRetries += log.attemptNumber;
        }

        const resolved = this.logs.filter(l => l.resolved).length;
        const failed = this.logs.filter(l => !l.resolved).length;

        return {
            total: this.logs.length,
            resolved,
            failed,
            byEntityType,
            byConflictType,
            averageRetries: this.logs.length > 0 ? totalRetries / this.logs.length : 0
        };
    }

    /**
     * Get recent conflict log entries.
     * 
     * @param limit - Maximum number of entries to return
     * @returns Array of recent log entries
     */
    getRecentLogs(limit: number = 50): ConflictLogEntry[] {
        return this.logs.slice(-limit);
    }

    /**
     * Get logs for a specific entity.
     * 
     * @param entityType - The entity type to filter by
     * @param entityId - Optional entity ID to filter by
     * @returns Array of matching log entries
     */
    getLogsForEntity(entityType: string, entityId?: string): ConflictLogEntry[] {
        return this.logs.filter(log => {
            if (log.entityType !== entityType) {
                return false;
            }
            if (entityId && log.entityId !== entityId) {
                return false;
            }
            return true;
        });
    }

    /**
     * Clear all logs.
     * Primarily for testing purposes.
     */
    clear(): void {
        this.logs = [];
    }

    /**
     * Print statistics to console.
     */
    printStats(): void {
        const stats = this.getStats();
        console.group('[Conflict Resolution] Statistics');
        console.log(`Total conflicts: ${stats.total}`);
        console.log(`Resolved: ${stats.resolved} (${((stats.resolved / stats.total) * 100).toFixed(1)}%)`);
        console.log(`Failed: ${stats.failed} (${((stats.failed / stats.total) * 100).toFixed(1)}%)`);
        console.log(`Average retries: ${stats.averageRetries.toFixed(2)}`);
        console.log('By entity type:', stats.byEntityType);
        console.log('By conflict type:', stats.byConflictType);
        console.groupEnd();
    }
}

/**
 * Global conflict logger instance.
 * Import this to log conflict resolution attempts.
 */
export const conflictLogger = new ConflictLogger();

/**
 * Start periodic metrics logging.
 * Logs conflict resolution statistics every 5 minutes.
 * 
 * @param intervalMs - Interval in milliseconds (default: 5 minutes)
 * @returns Function to stop periodic logging
 */
export function startPeriodicMetricsLogging(intervalMs: number = 5 * 60 * 1000): () => void {
    const intervalId = setInterval(() => {
        const stats = conflictLogger.getStats();
        if (stats.total > 0) {
            conflictLogger.printStats();
        }
    }, intervalMs);

    return () => clearInterval(intervalId);
}
